/**
 * Safe error logging utility for Hermes JavaScript engine.
 *
 * Prevents "Error.stack getter called with an invalid receiver" crashes
 * when external APIs return errors with pre-ES6 inheritance patterns.
 *
 * @see https://github.com/facebook/hermes/issues/1496
 * @see https://github.com/facebook/react-native/issues/43636
 */

import {crashlyticsService} from '../services/CrashlyticsService';
import {sentryService} from '../services/SentryService';

import dev from './dev';
import {logger} from './logger';

interface SafeErrorObject {
  name: string;
  message: string;
  stack?: string;
  code?: string | number;
  status?: number;
  [key: string]: any;
}

/**
 * Safely extracts error information without triggering Hermes .stack crash.
 *
 * @param error - Any value thrown in a catch block
 * @returns Serializable error object
 */
export function safeExtractError(error: unknown): SafeErrorObject {
  // Handle non-Error values (string, number, null, etc.)
  if (!error || typeof error !== 'object') {
    return {
      name: 'UnknownError',
      message: String(error),
    };
  }

  const safeError: SafeErrorObject = {
    name: 'Error',
    message: 'An error occurred',
  };

  // Extract name (safe)
  try {
    if ('name' in error && typeof error.name === 'string') {
      safeError.name = error.name;
    }
  } catch {
    // Ignore
  }

  // Extract message (safe)
  try {
    if ('message' in error && typeof error.message === 'string') {
      safeError.message = error.message;
    } else {
      safeError.message = String(error);
    }
  } catch {
    safeError.message = 'Error serialization failed';
  }

  // Extract stack (DANGEROUS in Hermes - must wrap)
  try {
    if ('stack' in error && typeof error.stack === 'string') {
      safeError.stack = error.stack;
    }
  } catch {
    safeError.stack = 'Stack trace unavailable (Hermes compatibility issue)';
  }

  // Extract common error properties (axios, fetch, etc.)
  const commonProps = ['code', 'status', 'statusCode', 'statusText', 'url', 'method'];
  for (const prop of commonProps) {
    try {
      if (prop in error) {
        safeError[prop] = (error as any)[prop];
      }
    } catch {
      // Ignore
    }
  }

  return safeError;
}

/**
 * Logs error safely to console with optional context.
 * Only includes stack traces in development environments.
 *
 * Uses structured logger with OTEL trace correlation.
 *
 * @param error - Error to log
 * @param context - Optional context string (e.g., "OffersStore.loadLinkedProducts")
 */
export function logError(error: unknown, context?: string): void {
  const safe = safeExtractError(error);

  const namespace = context || 'Error';

  if (dev.enableDebugging()) {
    // Development: full error with stack
    logger.error(namespace, safe.message, {
      name: safe.name,
      stack: safe.stack,
      ...safe,
    });
  } else {
    // Production: minimal error (security - don't expose stack traces)
    logger.error(namespace, safe.message, {
      name: safe.name,
      code: safe.code,
      status: safe.status,
    });

    // Send to Crashlytics (mobile only, production only)
    const errorObj = new Error(safe.message);
    errorObj.name = safe.name;
    if (safe.stack) {
      errorObj.stack = safe.stack;
    }
    crashlyticsService.recordError(errorObj, context);
    sentryService.captureException(errorObj, context);
  }
}

/**
 * Logs warning safely to console.
 *
 * Uses structured logger with OTEL trace correlation.
 *
 * @param error - Error to log as warning
 * @param context - Optional context string
 */
export function logWarning(error: unknown, context?: string): void {
  const safe = safeExtractError(error);
  const namespace = context || 'Warning';

  logger.warn(namespace, safe.message, {
    name: safe.name,
    code: safe.code,
  });
}

export default {
  safeExtractError,
  logError,
  logWarning,
};
